import React from 'react'
import Tooltip from '@mui/material/Tooltip'

const toothPositionOffsetY = 4

const getDynamicPositionStyle = (
  position,
  imageNaturalSize,
  positionOffsetY,
) => ({
  left: `${position.x}%`,
  top: `calc(${position.y}% + ${positionOffsetY}px)`,
  width: `${(position.w / imageNaturalSize.width) * 100}%`,
  aspectRatio: `${position.w} / ${position.h}`,
})

const getZoneButtonStyle = (position, isSelected) => ({
  position: 'absolute',
  left: `${position.x}%`,
  top: `${position.y}%`,
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
  border: '1px solid #007bff',
  borderRadius: 16,
  background: isSelected ? '#007bff' : 'rgba(255, 255, 255, 0.92)',
  color: isSelected ? '#fff' : '#007bff',
  padding: '4px 10px',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
})

const zoneButtonTooltip = (
  <div>
    <div>กดครั้งที่ 1 ฟันผู้ใหญ่</div>
    <div>กดครั้งที่ 2 ฟันเด็ก</div>
    <div>กดครั้งที่ 3 ยกเลิก</div>
  </div>
)

const TeethImageMap = ({
  image,
  imageAlt,
  imageNaturalSize,
  teeth,
  selectedTeeth = [],
  maxWidth,
  onToothClick,
  zoneButtons = [],
  onZoneClick,
  getZoneTooltip,
  activeZoneCode,
  isSelectable = true,
  selectableLabel,
  headerAction,
  ImageComponent = 'img',
  imageProps = {},
  positionOffsetY = toothPositionOffsetY,
}) => {
  const hasSelectableLabel = isSelectable && selectableLabel
  const hasHeader = hasSelectableLabel || headerAction
  const hasZoneButtons = isSelectable && zoneButtons.length > 0
  const hasActiveZoneCode =
    activeZoneCode !== undefined &&
    activeZoneCode !== null &&
    activeZoneCode !== ''
  const isSelectedToothList = (toothList = []) =>
    toothList.length > 0 &&
    toothList.every((toothValue) =>
      selectedTeeth.some(
        (selectedTooth) => String(selectedTooth.fdi) === String(toothValue),
      ),
    )

  return (
    <div
      style={{
        width: '100%',
        maxWidth,
        margin: '0 auto',
      }}
    >
      {hasHeader && (
        <div
          className="pt-3"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 24,
          }}
        >
          <div style={{ flex: 1 }} />
          {hasSelectableLabel && (
            <span
              style={{
                padding: '5px 14px',
                borderRadius: 16,
                background: '#f8fbff',
                border: '1px solid #d8e8ff',
                color: '#007bff',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {selectableLabel}
            </span>
          )}
          <div
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {headerAction}
          </div>
        </div>
      )}

      <div style={{ position: 'relative' }} className="pt-3">
        <ImageComponent
          src={image}
          alt={imageAlt}
          style={{ width: '100%', display: 'block', borderRadius: 8 }}
          {...imageProps}
        />

        {hasZoneButtons &&
          zoneButtons.map((zoneButton) => {
            const isSelectedZone =
              zoneButton.areaCode && hasActiveZoneCode
                ? String(zoneButton.areaCode) === String(activeZoneCode)
                : isSelectedToothList(zoneButton.adultToothList) ||
                  isSelectedToothList(zoneButton.childToothList) ||
                  isSelectedToothList(zoneButton.toothList)
            const zoneTooltip = zoneButton.areaCode
              ? `Area Code ${zoneButton.areaCode}`
              : getZoneTooltip
              ? getZoneTooltip(zoneButton)
              : zoneButtonTooltip

            return (
              <Tooltip
                key={zoneButton.label}
                title={zoneTooltip}
                arrow
                disableInteractive
                leaveDelay={0}
                placement="top"
              >
                <button
                  type="button"
                  aria-label={`Select ${zoneButton.label}`}
                  onClick={() => {
                    if (onZoneClick) onZoneClick(zoneButton)
                  }}
                  style={getZoneButtonStyle(
                    zoneButton.position,
                    isSelectedZone,
                  )}
                >
                  {zoneButton.label}
                </button>
              </Tooltip>
            )
          })}

        {teeth.map((tooth) => {
          const isSelected =
            isSelectable &&
            selectedTeeth.some(
              (selectedTooth) =>
                String(selectedTooth.fdi) === String(tooth.fdi),
            )

          return (
            <Tooltip
              key={tooth.id}
              title={`FDI ${tooth.fdi} : ${tooth.name}`}
              arrow
              disableInteractive
              leaveDelay={0}
              placement={tooth.placement}
            >
              <button
                type="button"
                aria-label={`Tooth ${tooth.id}`}
                aria-disabled={!isSelectable}
                onClick={() => {
                  if (isSelectable && onToothClick) onToothClick(tooth)
                }}
                style={{
                  position: 'absolute',
                  ...getDynamicPositionStyle(
                    tooth.position,
                    imageNaturalSize,
                    positionOffsetY,
                  ),
                  transform: 'translate(-50%, -50%)',
                  border: isSelected ? '2px solid #007bff' : '0',
                  borderRadius: 8,
                  background: isSelected
                    ? 'rgba(0, 123, 255, 0.25)'
                    : 'transparent',
                  cursor: isSelectable ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                  zIndex: 1,
                }}
              />
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}

export default TeethImageMap
